import type { MillageInputsForm } from '~/builders/millage-info/MillageInfoInputs';

export type MillagePhase = 'peak' | 'transition' | 'minimum';

interface MillagePhaseInfo {
  phase: MillagePhase;
  millage: number;
}

/**
 * Determines the millage phase and value for a given year based on millage inputs
 * @param year The year to check
 * @param startYear The starting year of the project
 * @param totalYears Total number of years in the project
 * @param millageInputs The millage input parameters
 * @returns Information about the millage phase and value for the given year
 */
export function getMillagePhaseForYear(
  year: number,
  startYear: number,
  totalYears: number,
  millageInputs: MillageInputsForm
): MillagePhaseInfo {
  const { peakMillage, minimumMillage, peakMillageDuration, minimumMillageDuration } =
    millageInputs;
  const yearsFromStart = year - startYear;

  // Convert string values to numbers for comparison
  const peakMillageNum = parseFloat(peakMillage) || 0;
  const minimumMillageNum = parseFloat(minimumMillage) || 0;
  const peakDurationNum = parseInt(peakMillageDuration, 10) || 0;
  const minimumDurationNum = parseInt(minimumMillageDuration, 10) || 0;

  // Handle peak millage period
  if (yearsFromStart < peakDurationNum) {
    return {
      phase: 'peak',
      millage: peakMillageNum,
    };
  }

  // Handle minimum millage period
  if (yearsFromStart >= totalYears - minimumDurationNum) {
    return {
      phase: 'minimum',
      millage: minimumMillageNum,
    };
  }

  // Must be in transition period
  return {
    phase: 'transition',
    millage: 0, // The actual millage value will be calculated by the shape generator
  };
}

/**
 * Gets the transition period information for calculating millage values
 * @param startYear The starting year of the project
 * @param totalYears Total number of years in the project
 * @param millageInputs The millage input parameters
 * @returns Information about the transition period
 */
export function getTransitionPeriodInfo(
  startYear: number,
  totalYears: number,
  millageInputs: MillageInputsForm
): {
  startYear: number;
  endYear: number;
  length: number;
  peakMillage: number;
  minimumMillage: number;
} {
  const { peakMillage, minimumMillage, peakMillageDuration, minimumMillageDuration } =
    millageInputs;

  // Convert string values to numbers
  const peakMillageNum = parseFloat(peakMillage) || 0;
  const minimumMillageNum = parseFloat(minimumMillage) || 0;
  const peakDurationNum = parseInt(peakMillageDuration, 10) || 0;
  const minimumDurationNum = parseInt(minimumMillageDuration, 10) || 0;

  // because the transition period includes a max year and a min year, we need to subtract 1 from the peak duration and add 1 to the minimum duration
  const transitionStartYear = startYear + peakDurationNum - 1;
  const transitionEndYear = startYear + totalYears - minimumDurationNum;
  const transitionLength = transitionEndYear - transitionStartYear + 1;

  return {
    startYear: transitionStartYear,
    endYear: transitionEndYear,
    length: transitionLength,
    peakMillage: peakMillageNum,
    minimumMillage: minimumMillageNum,
  };
}
